import React, { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Check, ChevronDown } from "lucide-react";
import { useAuth } from "../contexts/AuthContext";
import { setActiveRole } from "../api";
import showToast from "../utils/toast";
import "./RoleSwitcher.css";

// Active-role dropdown for users who were given more than one role in
// User Management (Add/Edit User > Role). Picking a role makes it the active
// one (POST /api/v1/users/me/active-role, which only accepts roles from the
// user's `roles`), then opens that role's dashboard; menus and permissions
// follow the active role. Single-role users get `fallback` (default nothing),
// so their screens stay exactly as before.
//
// variant "header" – pill button for a top bar (Layout header, Music/Sports
//                    coach header).
// variant "label"  – replaces a "(Coach)"-style role caption under the user's
//                    name (coach / medical incharge sidebars, purchase manager
//                    top bar).

// Same wording as the Role field in UserForm.js.
const ROLE_LABELS = {
  admin: "Admin",
  coach: "Coach",
  "balagruha-incharge": "Balagruha In-charge",
  "purchase-manager": "Purchase Manager",
  "medical-incharge": "Medical Incharge",
  "sports-coach": "Sports Coach",
  "music-coach": "Music Coach",
  amma: "Amma",
  student: "Student",
};

export const formatRoleLabel = (roleValue) =>
  ROLE_LABELS[roleValue] ||
  (roleValue || "")
    .split("-")
    .filter(Boolean)
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(" ");

export default function RoleSwitcher({ variant = "header", fallback = null }) {
  const { user, setActiveRoleLocal } = useAuth();
  const navigate = useNavigate();
  const rootRef = useRef(null);
  const [open, setOpen] = useState(false);
  const [isSwitching, setIsSwitching] = useState(false);

  useEffect(() => {
    if (!open) return undefined;
    const close = (event) => {
      if (event.type === "keydown" && event.key !== "Escape") return;
      if (event.type === "mousedown" && rootRef.current?.contains(event.target)) return;
      setOpen(false);
    };
    document.addEventListener("mousedown", close);
    document.addEventListener("keydown", close);
    return () => {
      document.removeEventListener("mousedown", close);
      document.removeEventListener("keydown", close);
    };
  }, [open]);

  const roles = Array.isArray(user?.roles) ? user.roles : [];
  if (roles.length < 2) return fallback;

  const activeRole = user?.role || localStorage.getItem("role") || "";

  const handleSelect = async (newRole) => {
    if (newRole === activeRole || isSwitching) {
      setOpen(false);
      return;
    }

    setIsSwitching(true);
    try {
      const result = await setActiveRole(newRole);
      setOpen(false);
      setActiveRoleLocal(newRole, result?.data?.roles);
      navigate("/dashboard");
    } catch (error) {
      console.error("Failed to switch active role:", error);
      showToast(
        error?.response?.data?.message || "Could not switch role. Please try again.",
        "error"
      );
    } finally {
      setIsSwitching(false);
    }
  };

  return (
    <div ref={rootRef} className={`role-switcher role-switcher--${variant}`}>
      <button
        type="button"
        className="role-switcher-toggle"
        aria-haspopup="menu"
        aria-expanded={open}
        aria-label={`Active role: ${formatRoleLabel(activeRole)}. Switch role`}
        title="Switch role"
        onClick={() => setOpen((prev) => !prev)}
        disabled={isSwitching}
      >
        <span className="role-switcher-current">{formatRoleLabel(activeRole)}</span>
        <ChevronDown
          size={variant === "label" ? 14 : 16}
          className={`role-switcher-caret${open ? " is-open" : ""}`}
          aria-hidden="true"
        />
      </button>
      {open && (
        <div className="role-switcher-options" role="menu" aria-label="Switch role">
          {roles.map((roleOption) => {
            const isActive = roleOption === activeRole;
            return (
              <button
                type="button"
                key={roleOption}
                role="menuitemradio"
                aria-checked={isActive}
                className={`role-switcher-option${isActive ? " active" : ""}`}
                onClick={() => handleSelect(roleOption)}
                disabled={isSwitching}
              >
                <span>{formatRoleLabel(roleOption)}</span>
                {isActive && <Check size={16} aria-hidden="true" />}
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
}
